import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from './swal';
import RoadiesShell from './RoadiesShell';
import { GameHead, TopBar, useSaveScore } from './common';
import { useGameTimer, useRotateNotice } from './hooks';

// s6.php: the nine clickable groups item1..item9 in s6-svg.svg, each worth 1 point once.
const LABELS = ['Ball', 'Sweets', 'Frame', 'Glasses', 'Clock', 'Decoration', 'Cake', 'Moon', 'Lamp'];
const MIN_PICKS = 5;

function Stage6Game() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const game = useSelector((s) => s.roadies.game);
  const rotate = useRotateNotice();
  const timer = useGameTimer(true, game?.time);
  const save = useSaveScore();

  const base = game?.totalScore || 0;
  const hostRef = useRef(null);
  const found = useRef(new Set());
  const pointsRef = useRef(base);
  const [svg, setSvg] = useState('');
  const [points, setPoints] = useState(base);
  const [spots, setSpots] = useState(Array(9).fill(''));
  const [picks, setPicks] = useState(0);
  const busy = useRef(false);

  useEffect(() => {
    let alive = true;
    fetch('/roadies/s6-svg.svg')
      .then((r) => r.text())
      .then((t) => alive && setSvg(t))
      .catch((e) => console.error('Stage 6 artwork failed to load', e));
    return () => {
      alive = false;
    };
  }, []);

  // Wire the nine item groups once the SVG is in the DOM.
  useEffect(() => {
    const host = hostRef.current;
    if (!host || !svg) return undefined;
    const handlers = [];
    for (let i = 0; i < 9; i++) {
      const el = host.querySelector(`.item${i + 1}`);
      if (!el) continue;
      el.style.cursor = 'pointer';
      const onClick = () => {
        if (found.current.has(i)) return;
        found.current.add(i);
        pointsRef.current += 1;
        setPoints(pointsRef.current);
        setSpots((prev) => {
          const next = [...prev];
          next[i] = LABELS[i];
          return next;
        });
        setPicks(found.current.size);
        el.style.pointerEvents = 'none';
        Swal.fire({
          icon: 'success',
          title: 'Great find!',
          html: `<b>${LABELS[i]}</b> is a round item that represents prosperity.`,
          timer: 3500,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      };
      el.addEventListener('click', onClick);
      el.addEventListener('touchstart', onClick, { passive: true });
      handlers.push([el, onClick]);
    }
    return () => handlers.forEach(([el, h]) => {
      el.removeEventListener('click', h);
      el.removeEventListener('touchstart', h);
    });
  }, [svg]);

  const submit = async () => {
    if (busy.current) return;
    if (found.current.size < MIN_PICKS) {
      Swal.fire('', `You must select at least ${MIN_PICKS} items before submitting!`, 'info');
      return;
    }
    busy.current = true;
    const score = base + found.current.size;
    const state = await save(6, score, timer.current());
    if (state) navigate({ pathname: '/stage7', search });
    else busy.current = false;
  };

  const spot = (v, i) => <input key={i} className="rd-spot" value={v} readOnly />;

  return (
    <>
      {rotate && <div className="rd-rotate">Please rotate your device for the best experience</div>}
      <TopBar time={timer.text} points={points} />
      <GameHead
        title="Philippines"
        highlight="Identify all round-shaped items from the image that could represent prosperity"
        note={`(Select at least ${MIN_PICKS} items from the image by clicking on them)`}
      >
        In the Philippines, New Year is all about money. Hoping to bring prosperity and wealth for the year
        ahead, Filipinos surround themselves with round things to represent coins and wealth.
      </GameHead>
      <div className="rd-game">
        {/* three answer boxes left, the picture, three right, and three underneath */}
        <div className="rd-s6">
          <div className="rd-s6-side rd-s6-left">{spots.slice(0, 3).map(spot)}</div>
          <div className="rd-svg-wrap" ref={hostRef} dangerouslySetInnerHTML={{ __html: svg }} />
          <div className="rd-s6-side rd-s6-right">{spots.slice(3, 6).map((v, i) => spot(v, i + 3))}</div>
          <div className="rd-s6-bottom">{spots.slice(6, 9).map((v, i) => spot(v, i + 6))}</div>
        </div>
        <div>
          <button type="button" className="rd-btn" onClick={submit} disabled={picks < MIN_PICKS}>Submit</button>
        </div>
      </div>
    </>
  );
}

export default function RoadiesStage6() {
  return (
    <RoadiesShell bg="phill.jpg" bgMob="phill-mob.jpg" stage={6} className="rd-stage rd-stage6">
      <Stage6Game />
    </RoadiesShell>
  );
}
